import type { PluralizationRule } from "vue-i18n";

/**
 * Plural rules for the locales whose grammar has a separate form for 2 to 4 ("few").
 *
 * Branch convention for these locales:
 * - 4 branches: `zero | one | few | many`.
 * - 2 or 3 branches: the vue-i18n default (`one | other` and `zero | one | other`), so values
 *   that do not have a few form yet keep rendering exactly as before.
 *
 * The `one` branch holds the count placeholder, because in ru and uk it also covers 21, 31 and so on.
 */
type SlavicCategory = "one" | "few" | "many";

const BRANCHES_WITH_FEW = 4;

function categoryCzechSlovak(count: number): SlavicCategory {
  if (count === 1) {
    return "one";
  }
  if (count >= 2 && count <= 4) {
    return "few";
  }
  return "many";
}

function isFewEnding(count: number): boolean {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  return lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14);
}

function categoryPolish(count: number): SlavicCategory {
  if (count === 1) {
    return "one";
  }
  if (isFewEnding(count)) {
    return "few";
  }
  return "many";
}

function categoryEastSlavic(count: number): SlavicCategory {
  if (count % 10 === 1 && count % 100 !== 11) {
    return "one";
  }
  if (isFewEnding(count)) {
    return "few";
  }
  return "many";
}

const BRANCH_INDEX: Record<SlavicCategory, number> = {
  one: 1,
  few: 2,
  many: 3,
};

function defaultRule(choice: number, choicesLength: number): number {
  if (choicesLength === 2) {
    return choice === 1 ? 0 : 1;
  }
  return Math.min(choice, 2);
}

function createSlavicRule(categorize: (count: number) => SlavicCategory): PluralizationRule {
  return (choice: number, choicesLength: number, orgRule?: PluralizationRule): number => {
    const count = Math.abs(choice);
    if (choicesLength !== BRANCHES_WITH_FEW) {
      return orgRule ? orgRule(count, choicesLength) : defaultRule(count, choicesLength);
    }
    if (count === 0) {
      return 0;
    }
    if (!Number.isInteger(count)) {
      return BRANCH_INDEX.many;
    }
    return BRANCH_INDEX[categorize(count)];
  };
}

export const pluralRules: Record<string, PluralizationRule> = {
  cs: createSlavicRule(categoryCzechSlovak),
  sk: createSlavicRule(categoryCzechSlovak),
  pl: createSlavicRule(categoryPolish),
  ru: createSlavicRule(categoryEastSlavic),
  uk: createSlavicRule(categoryEastSlavic),
};
